<template>
  <OverlayComponentWrapper ref="wrapper" v-slot="{ isHidden, rect, ...attrs }">
    <div
      class="flex transition-opacity"
      :class="{
        'flex-row-reverse': overlayState.anchorX === 'right',
        'opacity-0 delay-150 duration-150': !show,
        'duration-75': show,
      }"
      v-bind="attrs"
    >
      <TheNuxtLink
        class="transition-transform duration-300"
        :to="parsedTitle.href"
        :class="{
          'scale-125': show,
          'scale-100': !show,
          'origin-left': overlayState.anchorX === 'right',
          'origin-right': overlayState.anchorX === 'left',
        }"
        :style="{ width: `${rect.width}px`, height: `${rect.height}px` }"
      >
        <AsyncImage
          class="w-full aspect-[5/7] object-cover transition-[border-radius] duration-300"
          :class="{
            'duration-300 ease-out': show,
            'rounded-md duration-150 ease-in-out': !show,
            'rounded-l-md': show && overlayState.anchorX === 'left',
            'rounded-r-md': show && overlayState.anchorX === 'right',
          }"
          :src="parsedTitle.coverUrl"
          :alt="parsedTitle.title"
          @load="() => (imageLoaded = true)"
        />
      </TheNuxtLink>
      <div
        class="relative transition-[width,opacity,height,top] overflow-hidden"
        :class="{
          'w-0 opacity-0 duration-300 ease-in-out': !show,
          'w-96 duration-300 ease-out': show,
        }"
        :style="{
          height: show ? `${rect.height * 1.25}px` : `${rect.height}px`,
          top: show ? `${-rect.height * 0.125}px` : '0px',
        }"
      >
        <div
          class="dark:bg-neutral-900 bg-white overflow-auto p-2 flex flex-col justify-between w-96 h-full"
          :class="
            overlayState.anchorX === 'left' ? 'rounded-r-md' : 'rounded-l-md'
          "
        >
          <div>
            <NamiLink :to="parsedTitle.href" class="font-medium line-clamp-4">
              {{ parsedTitle.title }}
            </NamiLink>
            <p
              class="break-words text-xs text-neutral-700 dark:text-neutral-300 mt-2 line-clamp-8"
            >
              {{ parsedTitle.description }}
            </p>
          </div>
          <div class="flex justify-between items-center gap-2">
            <div class="flex gap-2 items-center">
              <NamiButton
                button-type="primary"
                small
                :disabled="readChapterLoading"
                :icon="IconBook"
                no-waves
                @button-click="startReadChapter"
              >
                Begin Reading
              </NamiButton>
              <NamiButton
                :button-type="isFollowing ? 'secondary' : 'primary'"
                small
                :disabled="followCheckPending"
                :loading="followCheckPending"
                :icon="IconBookmark"
                @button-click="() => followActions.toggle()"
                no-waves
              >
                {{ isFollowing ? "Unfollow" : "Follow" }}
              </NamiButton>
            </div>
            <Tooltip fixed text="More info" no-pointer-events>
              <BaseButtonSecondary
                text
                small
                pill
                :href="parsedTitle.href"
                class="block h-full"
              >
                <IconArrowRight />
              </BaseButtonSecondary>
            </Tooltip>
          </div>
        </div>
      </div>
    </div>
  </OverlayComponentWrapper>
</template>

<script setup lang="ts">
import { IconArrowRight, IconBookmark, IconBook } from "@tabler/icons-vue";
import { ReadMarker, Title, type TitleEntity } from "~/src/api";
import OverlayComponentWrapper from "~/components/overlay/OverlayComponentWrapper.vue";
import {
  isParsed,
  parseTitleForDisplay,
  type ParsedTitleOutput,
} from "~/utils/display/title";
import { useWindowSize } from "@vueuse/core";

const props = defineProps<{
  title: TitleEntity | ParsedTitleOutput;
}>();

const nuxtApp = useNuxtApp();
const sessionState = useSession();
const { width } = useWindowSize();
const overlayState = useOverlayState("titles-expand");
const auth = nuxtApp.$auth();
const app = nuxtApp.$app();
const settings = nuxtApp.$settings();

const wrapper = ref<InstanceType<typeof OverlayComponentWrapper>>();

// img src changing can take a while to render the image, only begin the animation when it's done
const imageLoaded = ref(false);
watch(
  () => props.title.id,
  (newId, oldId) => {
    if (newId !== oldId) {
      imageLoaded.value = false;
    }
  },
);

const show = computed(() => imageLoaded.value && !wrapper.value?.isHidden);

const parsedTitle = computed(() =>
  isParsed(props.title)
    ? props.title
    : parseTitleForDisplay(props.title, "med"),
);
const hasFetchedFollowStatus = computed(
  () => props.title.id in sessionState.value.titleFollowStatuses,
);
const isLoggedIn = computed(() => !!auth?.user);

watchEffect(() => {
  if (!wrapper.value) {
    return true;
  }

  if (wrapper.value.rect.right + 396 < width.value) {
    overlayState.value.anchorX = "left";
  } else {
    overlayState.value.anchorX = "right";
  }
});

const {
  model: isFollowing,
  refresh,
  actions: followActions,
  pending: followCheckPending,
} = useOptimisticAsync(
  async () => {
    if (!isLoggedIn.value) {
      return false;
    }

    if (hasFetchedFollowStatus.value) {
      return sessionState.value.titleFollowStatuses[props.title.id];
    }

    const isFollowing = await Title.getFollow(
      props.title.id,
      [],
      await getTokenOrThrow(),
    ).then((res) => !!res);

    sessionState.value.titleFollowStatuses[props.title.id] = isFollowing;

    return isFollowing;
  },
  {
    toggle: {
      handler: async () => {
        if (!isLoggedIn.value) {
          app?.openLoginRequiredModal();
          throw new Error("Not logged in.");
        }

        const followStatusBeforeOptimisticUpdate = !isFollowing.value;

        followStatusBeforeOptimisticUpdate
          ? await Title.unfollow(props.title.id, await getTokenOrThrow())
          : await Title.follow(
              props.title.id,
              { readingStatus: "reading", notification: "true" },
              await getTokenOrThrow(),
            );
      },
      optimisticUpdate: (_, initialValue) => !initialValue,
      onError: (_, __, initialValue) => initialValue,
    },
  },
  {
    initialValue: false,
    immediate: false,
  },
);

function goToFirstChapter(chaptersIdsInDescendingOrder: string[]) {
  const firstChapter = chaptersIdsInDescendingOrder.at(-1);

  if (firstChapter) {
    navigateTo(linkTo("chapter", firstChapter));
    return;
  }

  app?.prompt("There are no chapters in this title yet.", {
    icon: "wink",
    buttons: {
      cancel: {
        buttonType: "secondary",
        buttonText: "Close",
        text: true,
      },
      leave: {
        buttonType: "primary",
        buttonText: "Go to title",
        href: parsedTitle.value.href,
        text: true,
      },
    },
  });
}

const { pending: readChapterLoading, action: startReadChapter } = useAction2(
  async () => {
    assertDefined(settings);

    const [readMarkers, chaptersList] = await Promise.all([
      isLoggedIn.value
        ? ReadMarker.getReadMarkersForTitle(
            props.title.id,
            await getTokenOrThrow(),
          )
        : null,
      Title.getChaptersList(props.title.id, {
        translatedLanguages:
          settings.settings.platform.preferredTranslationLocales,
      }),
    ]);

    const chaptersIdsInDescendingOrder = chaptersList.attributes.list
      .flatMap((volume) => volume.chapters)
      .map((chapter) => chapter.id);

    if (!readMarkers) {
      goToFirstChapter(chaptersIdsInDescendingOrder);
      return;
    }

    const readChapterIds = Object.keys(readMarkers.attributes.readChapterIds);

    for (let i = 0; i < chaptersIdsInDescendingOrder.length; ++i) {
      if (!readChapterIds.includes(chaptersIdsInDescendingOrder[i])) {
        continue;
      }

      if (i === 0) {
        await app?.prompt(
          "You have already read the latest chapter for this title.",
          {
            icon: "wink",
            buttons: {
              cancel: {
                buttonType: "secondary",
                buttonText: "Close",
                text: true,
              },
              leave: {
                buttonType: "primary",
                buttonText: "Go to title",
                href: parsedTitle.value.href,
                text: true,
              },
            },
          },
        );

        return;
      }

      await navigateTo(linkTo("chapter", chaptersIdsInDescendingOrder[i - 1]));

      return;
    }

    goToFirstChapter(chaptersIdsInDescendingOrder);
  },
);

watch(
  () => props.title.id,
  () => refresh(),
  { immediate: true },
);
</script>
